import { GA4 } from 'react-ga4/types/ga4';
import { initializeBrowserPulse } from '@pulse/typescript';

import { isDevelopment } from '../../utils/env';

import {
  IAnalytics,
  IG4DefaultParams,
  INavigationClickParams,
  IPopupActionParams,
  IPopupOpenParams,
  IToolSelectParams,
  ICategorySelectParams,
  ICategorySwitchParams,
  ISectionLinkClick,
  IStickyBannerClick,
  IPageClick,
} from './types';

declare global {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Window {
    hj?: any;
    braze: any;
    dataLayer?: any[];
  }
}

class AnalyticsGTM {
  protected ga4Instance: GA4;

  constructor(public options: IAnalytics, ga4Instance: GA4) {
    this.ga4Instance = ga4Instance;

    if (this.analyticsEnabled) {
      this.initGA4();
      this.initPulse();
    }
  }

  get analyticsEnabled() {
    return !isDevelopment() && !!window;
  }

  public initGA4() {
    this.ga4Instance.set({ appVersion: this.options.VERSION });
    this.ga4Instance.send('pageview');
  }

  public sendGaEvent(action: string, params: IG4DefaultParams) {
    if (this.analyticsEnabled) {
      params = {
        ...params,
        tool_name: this.options.TOOL_NAME,
      };
      this.ga4Instance.event(action, params);
      this.sendPulseEvent(action, params);
    }
  }

  private initPulse() {
    initializeBrowserPulse({
      sdks: ['settings', 'tracker'],
      app: 'tools.picsart.com',
      config: {
        settings: {
          defaults: {},
        },
      },
    });
  }

  private sendPulseEvent<T>(event: string, parameters: T) {
    window.dataLayer.push({
      event,
      parameters,
      pulse: 'event',
    });
  }

  public sendBrazeEvent(name: string) {
    if ('braze' in window) {
      window.braze.logCustomEvent(name);
    } else {
      console.warn('Braze not initialized');
    }
  }

  public hjEvent(event: string) {
    if (!this.analyticsEnabled) {
      return;
    }

    if ('hj' in window) {
      window.hj.event(event);
    } else {
      console.warn('Hotjar not initialized');
    }
  }

  public hjStateChange(state: string) {
    if (!this.analyticsEnabled) {
      return;
    }

    if ('hj' in window) {
      window.hj.stateChange(state);
    } else {
      console.warn('Hotjar not initialized');
    }
  }

  public popupOpen(params: IPopupOpenParams) {
    this.sendGaEvent('popup_open', params);
    this.hjEvent('popup_open');
  }

  public popupAction(params: IPopupActionParams) {
    this.sendGaEvent('popup_action', params);
    this.hjEvent('popup_action');
  }

  public shareOpen(pageName: string) {
    this.sendGaEvent('share_open', {
      page_name: pageName,
    });
    this.hjEvent('share_open');
  }

  public sharePage(actionType: string, pageName: string) {
    this.sendGaEvent('page_share', {
      page_name: pageName,
      action_type: actionType,
    });
    this.hjEvent('page_share');
  }

  public footerLinkClicked(action_type: string, pageName = 'landing_page') {
    this.sendGaEvent('footer_click', {
      page_name: pageName,
      tool_name: this.options.TOOL_NAME,
      action_type: action_type,
    });
    this.hjEvent('footer_click');
  }

  public navigationClick(params: INavigationClickParams) {
    this.sendGaEvent('navigation_click', { ...params });
  }

  public searchBarClick(page_name = 'landing_page', tool_name: string) {
    this.sendGaEvent('search_bar_click', {
      page_name,
      tool_name,
    });
  }

  public searchBarType(page_name = 'landing_page', tool_name: string) {
    this.sendGaEvent('search_bar_type', {
      page_name,
      tool_name,
    });
  }

  public searchResultsClick(page_name = 'landing_page', tool_name: string, action_type: string) {
    this.sendGaEvent('search_results_click', {
      page_name,
      tool_name,
      action_type,
    });
  }

  public toolSelect(params: IToolSelectParams) {
    this.sendGaEvent('tool_select', { ...params });
  }

  public toolCategorySelect(params: ICategorySelectParams) {
    this.sendGaEvent('tool_category_select', { ...params });
  }

  public toolCategorySwitch(params: ICategorySwitchParams) {
    this.sendGaEvent('tool_category_switch', { ...params });
  }

  public moreTools(page_name: string) {
    this.sendGaEvent('more_tools_load', {
      page_name,
    });
  }

  public pageClick(params: IPageClick) {
    this.sendGaEvent('page_click', {
      ...params,
    });
  }

  public topPageAnchor(pageName: string) {
    this.sendGaEvent('top_page_anchor', { page_name: pageName });
  }

  public sectionLinkClick(params: ISectionLinkClick) {
    this.sendGaEvent('section_link_click', { ...params });
  }

  public stickyBannerClick(params: IStickyBannerClick) {
    this.sendGaEvent('sticky_banner_click', { ...params });
  }
}

export default AnalyticsGTM;
